/**
 * Trạng thái toàn cục nhẹ (Zustand) — TDD §9.2: người dùng hiện tại, tenant, quyền hiệu lực, chế độ (tenant/platform),
 * phiên hỗ trợ. KHÔNG chứa dữ liệu máy chủ nghiệp vụ (đó là việc của TanStack Query).
 */
import type { Permissions } from '@kovix/api-client';
import { create } from 'zustand';

export type ConsoleMode = 'tenant' | 'platform';

export interface SupportSession {
  ticket: string;
  reason: string;
  access: 'read' | 'write';
  expiresAt: string;
  originRole: string;
  /** R6.13 K-20: phiên platform-ops + tenant được hỗ trợ khi SPA đã đổi sang token phiên hỗ trợ. */
  sessionId?: string | undefined;
  tenantId?: string | undefined;
  /** true = SPA đang gọi API tenant bằng token mạo danh (iam ký); false/undefined = chỉ banner (chưa nhận token). */
  tokenActive?: boolean | undefined;
}

interface PlatformSnapshot {
  mode: ConsoleMode;
  tenantId: string | null;
  tenantName: string;
  permissions: Permissions | null;
}

/** KOVIX-2981 (RJ-POP-05 B8): vì sao phiên hỗ trợ kết thúc — `ended` = gateway 401 auth.support_session_ended (thu hồi/kết thúc ở platform-ops), `expired` = token hết hạn ở SPA, `left` = người dùng tự rời. */
export type SupportEndReason = 'ended' | 'expired' | 'left';

export interface SupportEnded {
  reason: SupportEndReason;
  ticket: string;
  sessionId: string | undefined;
  at: number;
}

interface SessionState {
  status: 'anonymous' | 'loading' | 'ready';
  mode: ConsoleMode;
  user: { name: string; email: string } | null;
  tenantId: string | null;
  tenantName: string;
  permissions: Permissions | null;
  support: SupportSession | null;
  /** R6.13 K-20: quyền/chế độ platform giữ lại khi vào phiên hỗ trợ — khôi phục ở endSupport. */
  platformSnapshot: PlatformSnapshot | null;
  /** KOVIX-2981 B8: phiên hỗ trợ đang dùng token vừa kết thúc ngoài ý muốn (thu hồi/hết hạn) — Shell hiện thông báo + đưa về phạm vi Platform rồi `ackSupportEnded`. */
  supportEnded: SupportEnded | null;
  ackSupportEnded: () => void;
  setReady: (
    u: { name: string; email: string },
    tenantId: string | null,
    perms: Permissions,
    mode: ConsoleMode,
  ) => void;
  setLoading: () => void;
  /** W9: phiên hỗ trợ (SupportSessionsPage) — chỉ trạng thái hiển thị + chặn nút ghi khi chỉ đọc. */
  setSupport: (s: SupportSession | null) => void;
  /**
   * R6.13 K-20: SPA đã nhận token phiên hỗ trợ (auth.setSupportToken) → chuyển sang chế độ tenant với quyền của phiên
   * (`/api/v1/iam/me/permissions` gọi bằng token phiên), giữ lại quyền platform để khôi phục.
   */
  enterSupport: (s: SupportSession, perms: Permissions) => void;
  /** Kết thúc/thu hồi/hết hạn phiên: bỏ banner, khôi phục chế độ + quyền platform. Người gọi tự `setSupportToken(null)`. `reason` ≠ 'left' khi đang dùng token → ghi `supportEnded`. */
  endSupport: (reason?: SupportEndReason) => void;
  can: (capability: string) => boolean;
  hasModule: (moduleCode: string | null | undefined) => boolean;
}

/**
 * KOVIX-2858 (RJ-TAD-09 bước 3, BR-18.22): '*' của Tenant Admin là "mọi quyền TRONG tenant" — không bao giờ phủ module control plane
 * `platform.*` (realm kovix-platform; gateway từ chối token tenant ở /api/v1/platform/** → 403 auth.platform_token_required). Trước đây
 * `can('platform.tenant.overview')` trả true với '*' nên deep-link /platform lọt qua RequireCapability rồi mọi API 403 (trang lỗi thay vì
 * AccessDeniedPage). Ngoại lệ duy nhất `platform.sod.exempt`: máy chủ (TenantPrincipal.hasCapability) coi '*' là có mã này (SodExemption).
 */
const PLATFORM_CAPABILITY_PREFIX = 'platform.';
const WILDCARD_PLATFORM_EXCEPTIONS: readonly string[] = ['platform.sod.exempt'];

export function wildcardCovers(capability: string): boolean {
  return !capability.startsWith(PLATFORM_CAPABILITY_PREFIX) || WILDCARD_PLATFORM_EXCEPTIONS.includes(capability);
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'anonymous',
  mode: 'tenant',
  user: null,
  tenantId: null,
  tenantName: '',
  permissions: null,
  support: null,
  platformSnapshot: null,
  supportEnded: null,
  ackSupportEnded: () => set({ supportEnded: null }),
  setLoading: () => set({ status: 'loading' }),
  setSupport: (support) => set({ support }),
  enterSupport: (support, permissions) => {
    const s = get();
    const snapshot = s.platformSnapshot ?? {
      mode: s.mode,
      tenantId: s.tenantId,
      tenantName: s.tenantName,
      permissions: s.permissions,
    };
    const tenantId = support.tenantId ?? null;
    set({
      platformSnapshot: snapshot,
      support: { ...support, tokenActive: true },
      permissions,
      mode: 'tenant',
      tenantId,
      tenantName: tenantId ? `Hỗ trợ · Tenant ${tenantId.slice(-4).toUpperCase()}` : s.tenantName,
    });
  },
  endSupport: (reason = 'left') => {
    const { platformSnapshot: snap, support } = get();
    // KOVIX-2981 B8: chỉ báo khi phiên đang DÙNG token bị thu hồi/hết hạn (người dùng tự rời thì không)
    const supportEnded: SupportEnded | null =
      support?.tokenActive && reason !== 'left'
        ? { reason, ticket: support.ticket, sessionId: support.sessionId, at: Date.now() }
        : get().supportEnded;
    if (snap)
      set({
        support: null,
        platformSnapshot: null,
        supportEnded,
        mode: snap.mode,
        tenantId: snap.tenantId,
        tenantName: snap.tenantName,
        permissions: snap.permissions,
      });
    else set({ support: null, supportEnded });
  },
  setReady: (user, tenantId, permissions, mode) =>
    set({
      status: 'ready',
      user,
      tenantId,
      permissions,
      mode,
      tenantName: tenantId ? `Tenant ${tenantId.slice(-4).toUpperCase()}` : 'KOVIX Platform',
    }),
  can: (capability) => {
    const p = get().permissions;
    if (!p) return false;
    if (get().support?.access === 'read' && isMutation(capability)) return false;
    return (p.capabilities.includes('*') && wildcardCovers(capability)) || p.capabilities.includes(capability);
  },
  hasModule: (moduleCode) => {
    if (!moduleCode) return true;
    const p = get().permissions;
    if (!p) return false;
    // 09/10/2026 (BF53-TEST hạ gói): module gói không có → tắt cả với Tenant Admin '*' (trước đây '*' luôn thấy menu, API thì 403)
    if (p.disabledModules?.includes(moduleCode)) return false;
    if (p.capabilities.includes('*')) return true;
    return p.modules.includes(moduleCode);
  },
}));

/**
 * Quy ước giống CapabilityAspect phía server (mã 3 phần module.aggregate.action): action view/export/self/overview
 * hoặc aggregate `dashboard` là đọc; còn lại là ghi — bị chặn trong phiên hỗ trợ chỉ đọc.
 */
function isMutation(capability: string) {
  const parts = capability.split('.');
  const action = parts[parts.length - 1] ?? '';
  const readAction = action === 'view' || action === 'export' || action === 'self' || action === 'overview';
  const dashboard = parts.length === 3 && parts[1] === 'dashboard';
  return !(readAction || dashboard);
}

export function useCan(capability: string): boolean {
  return useSession((s) => s.can(capability));
}

/**
 * KOVIX-110 (BF-O2C-13 B1) — quyền phải được cấp TƯỜNG MINH: khác `can()`, '*' của Tenant Admin KHÔNG tính.
 * Cùng quy tắc máy chủ `order-svc ExplicitCapability` (vd thuế suất phí giao `sales.delivery_fee.configure_tax` → Tenant Admin 403).
 */
export function hasExplicitCapability(
  permissions: Permissions | null,
  capability: string,
  support?: SupportSession | null,
): boolean {
  if (!permissions) return false;
  if (support?.access === 'read' && isMutation(capability)) return false;
  return permissions.capabilities.includes(capability);
}

export function useCanExplicitly(capability: string): boolean {
  return useSession((s) => hasExplicitCapability(s.permissions, capability, s.support));
}

/**
 * 06/10/2026 (người dùng chốt) — Admin tenant ('*') / Super admin (platform.sod.exempt, kể cả phiên hỗ trợ GHI) được tự duyệt chứng từ do chính mình lập:
 * máy chủ miễn SoD maker–checker (platform/tenancy SodExemption). Giao diện KHÔNG ẩn/khoá nút duyệt vì "bạn là người lập" với người này.
 */
export const SOD_EXEMPT_CAPABILITY = 'platform.sod.exempt';

export function useCanSelfApprove(): boolean {
  return useSession((s) => s.can(SOD_EXEMPT_CAPABILITY));
}
